import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useUploadStatementMutation } from '../../api';
import { useOnboarding } from '../../hooks/useOnboarding';
import {
	STATEMENT_UPLOAD_ACCEPT,
	STATEMENT_UPLOAD_HINT,
	sortStatementPhotos,
	statementFilesError,
} from '../../utils/statementUploadFormats';
import {
	Alert,
	Button,
	FileUploadDropzone,
	SelectInput,
	Typography,
} from '../common';

import type { RiskTier } from '../../api';

const OnboardingStatementUpload: React.FC = () => {
	const navigate = useNavigate();
	const {
		completeCurrentStep,
		completeStepById,
		uploadedFileId,
		updateUploadedFileId,
		isStepCompleted,
		startedAt,
	} = useOnboarding();
	const [uploadSuccess, setUploadSuccess] = useState(false);
	const [currentFileId, setCurrentFileId] = useState<string | null>(null);

	// Upload form state
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [dragActive, setDragActive] = useState(false);
	const [error, setError] = useState('');

	// Configuration state
	const [selectedRateTypes, setSelectedRateTypes] = useState<
		('IC_PLUS' | 'TIERED' | 'FLAT_TIERED')[]
	>(['IC_PLUS']);
	const [selectedRiskTier, setSelectedRiskTier] = useState<RiskTier>('MEDIUM');

	// Flat Tiered specific fields
	const [processorName, setProcessorName] = useState('');
	const [businessType, setBusinessType] = useState('specialty_retail');

	const [shouldGoToSubscription, setShouldGoToSubscription] = useState(false);

	const [uploadStatement, { isLoading: uploading }] =
		useUploadStatementMutation();

	// Check if step 1 is already completed on component mount
	useEffect(() => {
		if (isStepCompleted(1) && uploadedFileId) {
			// If step 1 is completed, redirect to main onboarding page
			navigate('/onboarding');
		}
	}, [isStepCompleted, uploadedFileId, navigate]);

	const handleDrag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			handleFiles(Array.from(e.dataTransfer.files));
		}
	};

	const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			handleFiles(Array.from(e.target.files));
		}
	};

	const handleFiles = (picked: File[]) => {
		const files = picked.length > 1 ? sortStatementPhotos(picked) : picked;
		const file = files[0];
		if (!file) return;
		if (files.length > 1 && selectedRateTypes.includes('FLAT_TIERED')) {
			setError('Upload one Excel or CSV file at a time.');
			return;
		}
		// Different file type validation based on selected rate types
		if (selectedRateTypes.includes('FLAT_TIERED')) {
			const validTypes = [
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
				'application/vnd.ms-excel', // .xls
				'text/csv', // .csv
			];
			const fileExtension = file.name.toLowerCase().split('.').pop();
			const isValidType =
				validTypes.includes(file.type) ||
				['xlsx', 'xls', 'csv'].includes(fileExtension || '');

			if (!isValidType) {
				setError('Please select an Excel (.xlsx) or CSV file only.');
				return;
			}
		} else {
			const filesError = statementFilesError(files);
			if (filesError) {
				setError(filesError);
				return;
			}
		}

		if (files.some((f) => f.size > 10 * 1024 * 1024)) {
			// 10MB limit per file
			setError('Each file must be less than 10MB.');
			return;
		}
		setError('');
		setSelectedFile(file);
		setSelectedFiles(files);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (shouldGoToSubscription) {
			navigate('/subscription');
			return;
		}
		if (!selectedFile) {
			return;
		}

		// Validate flat tiered required fields
		if (selectedRateTypes.includes('FLAT_TIERED')) {
			if (!processorName) {
				setError('Please select a payment processor for flat rate statements.');
				return;
			}
			if (!businessType) {
				setError('Please select a business type for flat rate statements.');
				return;
			}
		}

		setError('');

		try {
			const formData = new FormData();
			(selectedFiles.length ? selectedFiles : [selectedFile]).forEach((f) =>
				formData.append('file', f),
			);
			formData.append('statement_type', 'vendor_statement');

			// Append rate plan type (use first selected type for backward compatibility)
			formData.append('rate_plan_type', selectedRateTypes[0]);

			// Append selected risk tier for revenue calculation
			formData.append('selected_risk_tier', selectedRiskTier);

			// Append flat tiered specific fields
			if (selectedRateTypes.includes('FLAT_TIERED')) {
				formData.append('processor_name', processorName);
				formData.append('business_type', businessType);
			}

			const result = await uploadStatement({
				formData,
				uploadConfig: {},
			}).unwrap();

			// Handle success
			setCurrentFileId(result.file_id);
			setUploadSuccess(true);

			// Update the uploaded file ID in Redux and complete the step
			updateUploadedFileId(result.file_id);

			// Auto-complete the upload step when file upload is successful
			// if (startedAt && !isStepCompleted(1)) {
			// 	completeStepById(1);
			// }

			// Redirect directly to analysis view instead of showing stepper
			// setTimeout(() => {
			// 	navigate(`/onboarding/analysis/${result.file_id}`);
			// }, 1500);
		} catch (err: any) {
			console.log('err', err);
			if (err.status === 402) {
				setShouldGoToSubscription(true);
			}
			setError(err?.data?.message || 'Upload failed. Please try again.');
		}
	};

	const removeFile = () => {
		setSelectedFile(null);
		setSelectedFiles([]);
		setError('');
	};

	const handleContinueToNextStep = () => {
		// Navigate back to the main stepper page
		navigate('/onboarding');
	};

	// If upload is successful and we have a file ID, show success message and redirect to analysis
	if (uploadSuccess && currentFileId) {
		return (
			<div className="max-w-2xl mx-auto text-center">
				<Alert
					variant="success"
					title="Statement Uploaded Successfully!"
					className="mb-6"
				>
					<Typography variant="body1" color="inherit">
						We're now analyzing your statement. This usually takes just a few
						minutes.
					</Typography>
				</Alert>

				<Alert variant="info" className="mb-6">
					<Typography variant="body2" color="inherit">
						<strong>Next:</strong> You'll be automatically taken to the analysis
						results where you can see your potential savings and choose a plan.
					</Typography>
				</Alert>

				<div className="text-center">
					<Typography variant="body2" color="muted" className="mb-4">
						Redirecting to analysis view in a few seconds...
					</Typography>
					<Button
						onClick={() => navigate(`/onboarding/analysis/${currentFileId}`)}
						variant="primary"
						size="lg"
					>
						Go to Analysis Now
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="px-4 py-10 sm:px-6 lg:px-8">
			<div className="mx-auto mb-10 max-w-[700px] text-center">
				<Typography variant="title" className="mb-4">
					Upload Your First Statement - We'll handle the rest
				</Typography>
				<Typography
					variant="body1"
					color="muted"
					className="max-w-2xl mx-auto leading-relaxed"
				>
					Drop in your first statement and we'll instantly break down your fees
					into a clear, easy-to-read report.
				</Typography>
			</div>

			<div className="mx-auto w-full max-w-[700px] bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-200 shadow-lg">
				<div className="flex items-center gap-2.5 p-5">
					<div className="flex-1 text-left text-black text-lg leading-normal">
						Upload{' '}
						{selectedRateTypes.includes('FLAT_TIERED')
							? 'Transactional'
							: 'PDF'}{' '}
						Statement
					</div>
				</div>

				<form onSubmit={handleSubmit} className="flex flex-col gap-5 px-5 pb-5">
					{/* Fee Structure Config Selection */}
					<div className="flex flex-col gap-4">
						{/* Rate Plan Type Selection */}
						<SelectInput
							label="Statement Type"
							value={selectedRateTypes[0] || 'IC_PLUS'}
							options={[
								{ label: 'Interchange Plus', value: 'IC_PLUS' },
								{ label: 'Tiered', value: 'TIERED' },
								{ label: 'Flat Rate (Transactional)', value: 'FLAT_TIERED' },
							].map((config) => ({
								value: config.value || '',
								label: config.label,
								isDefault: config.value === 'IC_PLUS',
							}))}
							onChange={(value) => {
								setSelectedRateTypes([
									value as 'IC_PLUS' | 'TIERED' | 'FLAT_TIERED',
								]);
								// Keep the picked file: every statement type accepts the same formats.
								setError('');
								// Reset flat tiered fields
								setProcessorName('');
								setBusinessType('specialty_retail');
							}}
							placeholder="Select Statement Type"
							selectClassName="bg-white"
							fullWidth={true}
						/>

						{/* Risk Tier Selection for Revenue Calculation */}
						<SelectInput
							label="Merchant Risk Tier (for ISO profit analysis)"
							value={selectedRiskTier}
							options={[
								{ value: 'LOW', label: 'Low Risk', isDefault: false },
								{ value: 'MEDIUM', label: 'Medium Risk', isDefault: true },
								{ value: 'HIGH', label: 'High Risk', isDefault: false },
							]}
							onChange={(value) => setSelectedRiskTier(value as RiskTier)}
							placeholder="Select Risk Tier"
							selectClassName="bg-white"
							disabled={uploading}
							helperText="Used to calculate agent profit margins based on Schedule A buy rates"
						/>

						{/* Flat Tiered Specific Fields */}
						{selectedRateTypes.includes('FLAT_TIERED') && (
							<div className="flex flex-col gap-4 p-4 bg-blue-50 rounded-md border border-blue-200">
								<div className="text-sm font-medium text-blue-900">
									Transaction Statement Details
								</div>
								<div className="flex flex-col gap-4">
									<SelectInput
										label="Payment Processor *"
										value={processorName}
										options={[
											{ value: 'Stripe', label: 'Stripe', isDefault: false },
											{ value: 'Square', label: 'Square', isDefault: false },
											{ value: 'PayPal', label: 'PayPal', isDefault: false },
											{
												value: 'Authorize.net',
												label: 'Authorize.net',
												isDefault: false,
											},
											{
												value: 'Braintree',
												label: 'Braintree',
												isDefault: false,
											},
											{ value: 'Other', label: 'Other', isDefault: false },
										]}
										onChange={setProcessorName}
										placeholder="Select payment processor"
										selectClassName="bg-white"
										disabled={uploading}
										helperText="Select the payment processor that generated this transaction file"
									/>
									<SelectInput
										label="Business Type *"
										value={businessType}
										options={[
											{
												value: 'specialty_retail',
												label: 'Retail (Card Present)',
												isDefault: true,
											},
											{
												value: 'restaurants',
												label: 'Restaurant/Food Service',
												isDefault: false,
											},
											{
												value: 'ecommerce',
												label: 'E-commerce/Online',
												isDefault: false,
											},
											{
												value: 'card_not_present',
												label: 'Card Not Present/MOTO',
												isDefault: false,
											},
										]}
										onChange={setBusinessType}
										placeholder="Select business type"
										selectClassName="bg-white"
										disabled={uploading}
										helperText="Select your primary business type for accurate interchange estimates"
									/>
								</div>
							</div>
						)}
					</div>

					{/* File Upload Area */}
					<div>
						<FileUploadDropzone
							title="Drag and drop file here"
							description={
								selectedRateTypes.includes('FLAT_TIERED')
									? 'Excel (.xlsx) or CSV files only, max 10MB'
									: STATEMENT_UPLOAD_HINT
							}
							buttonText="Choose File"
							accept={
								selectedRateTypes.includes('FLAT_TIERED')
									? '.xlsx,.xls,.csv'
									: STATEMENT_UPLOAD_ACCEPT
							}
							maxSize={10}
							onFileSelect={(f) => handleFiles([f])}
							onFilesSelect={handleFiles}
							selectedFiles={selectedFiles}
							onFileRemove={removeFile}
							selectedFile={selectedFile}
							dragActive={dragActive}
							onDragEnter={handleDrag}
							onDragLeave={handleDrag}
							onDragOver={handleDrag}
							onDrop={handleDrop}
							disabled={uploading}
							error={error}
							containerClassName="outline-zinc-400"
							iconClassName="text-[#ACB1B7]"
							titleClassName="text-black"
							descriptionClassName="text-zinc-500"
							buttonClassName="bg-gray-100 hover:bg-gray-200"
						/>
					</div>

					{/* Actions */}
					<div className="flex items-center gap-2.5 pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => navigate('/onboarding')}
							disabled={uploading}
							className="flex-1 h-10 px-5 bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-200 text-zinc-800 text-sm font-medium"
							size="lg"
						>
							Cancel
						</Button>
						<Button
							type="submit"
							variant="primary"
							disabled={uploading || !selectedFile}
							loading={uploading}
							className="flex-1 h-10 px-5 bg-primary rounded-md text-white text-sm font-medium"
							size="lg"
						>
							{shouldGoToSubscription ? 'Purchase Credits' : 'Upload Statement'}
						</Button>
					</div>
				</form>
			</div>

			<Alert
				variant="info"
				title="What happens next?"
				className="mx-auto mt-10 max-w-[700px]"
			>
				<ul className="space-y-2 text-left">
					<li className="flex items-start">
						<span className="text-blue-600 mr-2 mt-1">•</span>
						We'll analyze your statement for processing fees and interchange
						rates
					</li>
					<li className="flex items-start">
						<span className="text-blue-600 mr-3 mt-1">•</span>
						You'll get a detailed breakdown of potential savings
					</li>
					<li className="flex items-start">
						<span className="text-blue-600 mr-3 mt-1">•</span>
						Our AI will identify optimization opportunities
					</li>
				</ul>
			</Alert>
		</div>
	);
};

export default OnboardingStatementUpload;
